import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loginUser, selectUserInfo, selectAuthLoading, clearAuthError, clearSignupMessage } from "@redux/slices/authSlice";
import { authService, forgotPassword, verifyOtp, resetPasswordWithOtp } from "@services/httpClient";

// ─── Forgot Password Modal ─────────────────────────────────────────────────────
const ForgotPasswordModal = ({ onClose }) => {
    const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password, 4=success
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({ new: '', confirm: '' });
    const otpRefs = useRef([]);

    const handleSendOtp = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setError('');
        if (!email.trim()) { setError('Please enter your email address.'); return; }
        setLoading(true);
        try {
            await forgotPassword(email.trim().toLowerCase());
            setStep(2);
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to send OTP. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (idx, val) => {
        if (!/^\d?$/.test(val)) return;
        const next = [...otp];
        next[idx] = val;
        setOtp(next);
        if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    };

    const handleOtpKeyDown = (idx, e) => {
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1]?.focus();
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError('');
        const code = otp.join('');
        if (code.length < 6) { setError('Please enter the complete 6-digit OTP.'); return; }
        setLoading(true);
        try {
            await verifyOtp(email.trim().toLowerCase(), code);
            setStep(3);
        } catch (err) {
            setError(err?.response?.data?.error || 'Incorrect OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (passwordErrors.new || passwordErrors.confirm) {
            setError('Please fix the password errors.');
            return;
        }

        if (!newPassword || newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            await resetPasswordWithOtp(email.trim().toLowerCase(), otp.join(''), newPassword, confirmPassword);
            setSuccess('Password reset successfully! You can now sign in.');
            setStep(4);
        } catch (err) {
            setError(err?.response?.data?.error || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const validateNewPassword = (val) => {
        let error = "";
        if (!val) {
            error = "Password is required";
        } else if (val.length < 8) {
            error = "Minimum 8 characters required";
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(val)) {
            error = "Must include A-Z, a-z, 0-9, and special char";
        } else if (/(.)\1{2,}/.test(val)) {
            error = "No more than 2 same characters continuously allowed";
        } else if (/([@$!%*?&])\1/.test(val)) {
            error = "Consecutive identical special characters (like @@, !!) not allowed";
        }
        return error;
    };

    const handleNewPasswordChange = (val) => {
        const cleaned = val.replace(/\s/g, "");
        setNewPassword(cleaned);
        const error = validateNewPassword(cleaned);
        setPasswordErrors(prev => ({
            ...prev,
            new: error,
            confirm: confirmPassword && cleaned !== confirmPassword ? "Passwords do not match" : ""
        }));
    };

    const handleConfirmPasswordChange = (val) => {
        const cleaned = val.replace(/\s/g, "");
        setConfirmPassword(cleaned);
        setPasswordErrors(prev => ({
            ...prev,
            confirm: cleaned !== newPassword ? "Passwords do not match" : ""
        }));
    };

    const stepTitles = ['', 'Forgot Password', 'Enter OTP', 'New Password', 'Success!'];
    const stepSubtitles = ['', 'Enter your registered email address.', `OTP sent to ${email}`, 'Set your new password.', ''];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Clean header — no blue gradient */}
                <div className="px-6 pt-6 pb-2 border-b border-gray-100 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition text-2xl font-light leading-none"
                        type="button"
                    >×</button>
                    <h2 className="text-base font-semibold text-gray-900">{stepTitles[step]}</h2>
                    <p className="text-xs text-gray-500 mt-0.5">{stepSubtitles[step]}</p>
                </div>

                <div className="px-6 py-5">
                    {error && (
                        <div className="mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
                            <span className="text-red-500 mt-0.5">⚠</span> {error}
                        </div>
                    )}

                    {/* Step 1: Email */}
                    {step === 1 && (
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <input
                                type="email"
                                value={email}
                                onChange={e => { setEmail(e.target.value); setError(''); }}
                                placeholder="Enter your registered email"
                                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                required
                                autoFocus
                                disabled={loading}
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-50"
                            >
                                {loading ? 'Sending OTP...' : 'Send OTP'}
                            </button>
                        </form>
                    )}

                    {/* Step 2: OTP */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOtp} className="space-y-5">
                            <div className="flex gap-2 justify-center">
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={el => otpRefs.current[idx] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOtpChange(idx, e.target.value)}
                                        onKeyDown={e => handleOtpKeyDown(idx, e)}
                                        className="w-11 h-12 text-center text-xl font-bold border-2 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition bg-gray-50"
                                        autoFocus={idx === 0}
                                    />
                                ))}
                            </div>
                            <button
                                type="submit"
                                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition"
                            >
                                Verify OTP
                            </button>
                            <button
                                type="button"
                                onClick={() => { setOtp(['', '', '', '', '', '']); setError(''); handleSendOtp({}); }}
                                className="w-full text-xs text-blue-500 hover:text-blue-700 transition"
                                disabled={loading}
                            >
                                {loading ? 'Resending...' : "Didn't receive it? Resend OTP"}
                            </button>
                        </form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={e => handleNewPasswordChange(e.target.value)}
                                    placeholder="New password"
                                    className={`w-full rounded-md border ${passwordErrors.new ? 'border-red-500' : 'border-gray-300'} bg-gray-50 px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`}
                                    required
                                    autoFocus
                                    disabled={loading}
                                />
                                {passwordErrors.new && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 leading-tight">{passwordErrors.new}</p>}
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                    tabIndex="-1"
                                >
                                    {showNewPassword ? (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={e => handleConfirmPasswordChange(e.target.value)}
                                    placeholder="Confirm new password"
                                    className={`w-full rounded-md border ${passwordErrors.confirm ? 'border-red-500' : 'border-gray-300'} bg-gray-50 px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none`}
                                    required
                                    disabled={loading}
                                />
                                {passwordErrors.confirm && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 leading-tight">{passwordErrors.confirm}</p>}
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                    tabIndex="-1"
                                >
                                    {showConfirmPassword ? (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-50"
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    )}

                    {/* Step 4: Success */}
                    {step === 4 && (
                        <div className="text-center space-y-4 py-2">
                            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-sm text-gray-700 font-medium">{success}</p>
                            <button
                                onClick={onClose}
                                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition"
                            >
                                Back to Sign In
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Login Page ────────────────────────────────────────────────────────────────
const Login = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [alertMessage, setAlertMessage] = useState('');
    const [showForgot, setShowForgot] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userInfo = useSelector(selectUserInfo);
    const loading = useSelector(selectAuthLoading);
    const signupMessageFromState = useSelector(state => state.auth.signupMessage, (left, right) => left === right);

    const [displayableSignupMessage, setDisplayableSignupMessage] = useState(null);

    useEffect(() => {
        if (signupMessageFromState) {
            setDisplayableSignupMessage(signupMessageFromState);
            dispatch(clearSignupMessage());
        }
    }, [signupMessageFromState, dispatch]);

    useEffect(() => {
        const accessToken = authService.getAccessToken();
        if (userInfo && accessToken) {
            localStorage.setItem("accessToken", accessToken);
            if (userInfo.force_password_reset) {
                navigate('/force-password-reset');
            } else {
                navigate('/');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setAlertMessage('');
        dispatch(clearAuthError());
        const email = formData.email;
        const password = formData.password;
        
        if (/\s/.test(formData.password)) {
            toast.error("Spaces are not allowed in passwords.");
            return;
        }

        try {
            await dispatch(loginUser({ email, password })).unwrap();
        } catch (error) {
            setAlertMessage(error?.message || 'Invalid username or password');
        }
    };

    const handleChange = React.useCallback((e) => {
        setFormData(prevData => ({ ...prevData, [e.target.name]: e.target.value }));
    }, []);

    return (
        <>
            {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

            <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-900 p-12">
                <form onSubmit={handleSubmit}>
                    <div className="w-[384px] max-w-sm rounded-3xl bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 shadow-[0_0_20px_rgba(59,130,246,0.15)] p-px">
                        <div className="bg-white rounded-[calc(1.5rem-1px)] p-12 px-10">
                            <div className="flex flex-col items-center">
                                <h1 className="text-xl font-semibold text-gray-900 mt-3">
                                    Signin to your account
                                </h1>
                                {displayableSignupMessage && (
                                    <p className="mt-2 text-sm text-green-600">{displayableSignupMessage}</p>
                                )}
                            </div>

                            <div className="mt-8 space-y-8">
                                <div className="space-y-6">
                                    <input
                                        className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                        placeholder="Your Email"
                                        type="email"
                                        name="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={loading}
                                    />

                                    <div>
                                        <div className="flex justify-end mb-1">
                                            <button
                                                type="button"
                                                onClick={() => setShowForgot(true)}
                                                className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input
                                                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                                placeholder="Your Password"
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                id="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                                tabIndex="-1"
                                            >
                                                {showPassword ? (
                                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full rounded-lg bg-blue-600 px-12 py-2.5 font-semibold text-white outline-none transition-all hover:bg-blue-700 disabled:opacity-50 lg:px-16 lg:py-3 shadow-lg active:scale-95"
                                    disabled={loading}
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </button>

                                {alertMessage && (
                                    <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 mr-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-bold">{alertMessage}</span>
                                            </div>
                                            <button
                                                onClick={() => setAlertMessage('')}
                                                className="ml-2 text-red-400 hover:text-red-600 focus:outline-none transition-colors"
                                                type="button"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default Login;